'use client';

import { createElement } from 'react';
import classNames from '@/utils/classNames';
import LoadingPlaceholder from './LoadingPlaceholder';
import type { ElementType, ReactNode, ComponentPropsWithRef } from 'react';
export type BaseButtonProps<T extends ElementType> = {
  as?: T;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  loadingLight?: boolean;
  size?: 'xss' | 'xs' | 'small' | 'normal';
  shape?: 'normal' | 'rounded' | 'circular';
  className?: string;
  loadingClassName?: string;
  children?: ReactNode;
  icon?: any;
} & ComponentPropsWithRef<T>;
const BaseButton = <T extends ElementType,>({
  as,
  className,
  size,
  shape,
  loading,
  children,
  loadingClassName,
  loadingText,
  loadingLight,
  type = 'button',
  ref,
  ...props
}: BaseButtonProps<T>) => {
  const xss = size === 'xss';
  const xs = size === 'xs';
  const small = size === 'small';
  const rounded = shape === 'rounded';
  const circular = shape === 'circular';
  const baseClassNames = classNames(className, {
    'py-1': xs && !circular,
    'py-1.5': small && !circular,
    'py-2.5': !small && !circular,
    'px-3.5': !small && !rounded && !circular,
    'px-2.5': (small || xs) && !rounded && !circular,
    'px-2': xs && rounded,
    'px-4': !small && rounded,
    'px-3': small && rounded,
    'p-2': circular && !small,
    'p-1.5': circular && small,
    'p-1': circular && xs,
    'p-0': xss && circular
  });
  const mergeProps = {
    type,
    ...props
  };
  return createElement(as as ElementType || 'button', {
    ref,
    className: classNames('inline-flex items-center justify-center gap-x-2 text-sm font-semibold shadow-xs not-disabled:hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 not-disabled:active:shadow-xs', {
      'min-w-[127px]': !circular && !xs,
      'rounded-full': rounded || circular,
      'rounded-md': !rounded && !circular,
      'relative cursor-progress overflow-hidden': loading,
      'cursor-not-allowed': props?.disabled,
      'text-xs': xs
    }, baseClassNames),
    type,
    ...(loading ? {
      'data-loading': true
    } : {}),
    ...props
  }, <>
      {children}
      {loading && <LoadingPlaceholder className={classNames(baseClassNames, loadingClassName)} circular={circular} light={loadingLight}>
          {!circular && loadingText}
        </LoadingPlaceholder>}
    </>);
};
BaseButton.displayName = 'BaseButton';
export default BaseButton;